import type { GpuRenderContext, RenderContext, VideoFrame } from './types';
import { createGpuContext } from './gpu/shader';
import { TexturePool } from './gpu/texturePool';
import { FrameCache } from './gpu/frameCache';

function mediaIntrinsicSize(
  source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement,
): { w: number; h: number } {
  if (source instanceof HTMLVideoElement) {
    return { w: source.videoWidth || 0, h: source.videoHeight || 0 };
  }
  if (source instanceof HTMLImageElement) {
    return { w: source.naturalWidth || source.width || 0, h: source.naturalHeight || source.height || 0 };
  }
  return { w: source.width || 0, h: source.height || 0 };
}

/**
 * Capture pixels from a video/canvas/image into a VideoFrame.
 * Default fit is `contain` so preview/export never horizontally stretch the picture
 * when the target canvas aspect differs from the media.
 */
export function captureVideoFrame(
  source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement,
  width: number,
  height: number,
  opts: { fit?: 'contain' | 'cover' | 'fill' } = {},
): VideoFrame {
  const w = Math.max(2, Math.round(width));
  const h = Math.max(2, Math.round(height));
  const fit = opts.fit || 'contain';
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');

  if (source instanceof HTMLVideoElement) {
    if (!source.videoWidth || source.videoHeight < 1) {
      throw new Error('Video frame not ready');
    }
  }

  try {
    const { w: sw, h: sh } = mediaIntrinsicSize(source);
    if (fit === 'fill' || !(sw > 0 && sh > 0)) {
      ctx.drawImage(source, 0, 0, w, h);
    } else {
      const scale = fit === 'cover'
        ? Math.max(w / sw, h / sh)
        : Math.min(w / sw, h / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      const dx = (w - dw) / 2;
      const dy = (h - dh) / 2;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(source, 0, 0, sw, sh, dx, dy, dw, dh);
    }
  } catch (err) {
    throw new Error(`Failed to capture media frame: ${err instanceof Error ? err.message : 'drawImage failed'}`);
  }
  return { width: w, height: h, canvas, texture: null };
}

export function cloneVideoFrame(frame: VideoFrame): VideoFrame {
  const canvas = document.createElement('canvas');
  canvas.width = frame.width;
  canvas.height = frame.height;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
  }
  return { width: frame.width, height: frame.height, canvas, texture: null };
}

export function ensureFrameDimensions(frame: VideoFrame, w: number, h: number): VideoFrame {
  if (frame.width === w && frame.height === h) return frame;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0, w, h);
  }
  return { width: w, height: h, canvas, texture: null };
}

let sharedGpu: GpuRenderContext | null = null;
let sharedCanvas: HTMLCanvasElement | null = null;

export function getOrCreateGpuContext(): GpuRenderContext | null {
  if (sharedGpu) return sharedGpu;
  if (typeof document === 'undefined') return null;
  sharedCanvas = document.createElement('canvas');
  sharedCanvas.width = 1920;
  sharedCanvas.height = 1080;
  const ctx = createGpuContext(sharedCanvas);
  if (!ctx) return null;
  sharedGpu = {
    gl: ctx.gl,
    texturePool: new TexturePool(ctx.gl),
    frameCache: new FrameCache(48),
  };
  return sharedGpu;
}

export function disposeGpuContext(): void {
  if (sharedGpu) {
    sharedGpu.texturePool.dispose();
    sharedGpu.frameCache.clear();
    sharedGpu = null;
  }
  sharedCanvas = null;
}

export function buildRenderContext(
  currentTime: number,
  frameIndex: number,
  fps: number,
  mode: 'preview' | 'export',
  previousFrames: VideoFrame[],
  effectStart: number,
  effectEnd: number,
  seed = 0,
): RenderContext {
  return {
    currentTime,
    frameIndex,
    fps,
    mode,
    seed,
    previousFrames,
    // Preview uses CPU fallbacks (reliable with cross-origin media); export uses GPU.
    gpu: mode === 'export' ? getOrCreateGpuContext() : null,
    effectStartTime: effectStart,
    effectEndTime: effectEnd,
  };
}

export function localEffectTime(context: RenderContext): number {
  return Math.max(0, context.currentTime - context.effectStartTime);
}
